import { router } from "@inertiajs/react";
import { Button, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import type { DataTableSortStatus } from "mantine-datatable";
import { useState } from "react";
import { YearContextsTable } from "../../components/yearContext/YearContextsTable";
import { showDeleteModal } from "../../helpers/modal";
import { getYearString } from "../../helpers/stringHelper";
import { useYearContext } from "../../hooks/useYearContext";
import { ContentShell } from "../../layout/ContentShell";
import type { YearContext } from "../../models/yearContext";
import { edit_year_context_path, new_year_context_path, year_context_path, year_contexts_path } from "../../routes";

type YearContextsProps = {
  years: YearContext[];
  filter: {
    sort_key?: "starts_at" | "ends_at";
    sort_direction?: "asc" | "desc";
  };
};

export default function YearContexts({ years, filter }: YearContextsProps) {
  const { pathWithYearContext } = useYearContext();
  const [isFetching, setIsFetching] = useState(false);

  const onNew = () => router.get(pathWithYearContext(new_year_context_path));
  const onEdit = (year: YearContext) => router.get(pathWithYearContext(edit_year_context_path, year.id));
  const onDelete = (year: YearContext) =>
    showDeleteModal({
      title: `Jahrgang ${getYearString(year)}`,
      article: "den",
      additionalText: <Text fw="500">Sämtliche mit diesem Jahrgang verknüpften Daten werden gelöscht!</Text>,
      onConfirm: () => router.delete(pathWithYearContext(year_context_path, year.id)),
    });

  const sortStatus: DataTableSortStatus<YearContext> = {
    columnAccessor: filter?.sort_key || "",
    direction: filter?.sort_direction || "asc",
  };

  const sortChange = (sortStatus: DataTableSortStatus<YearContext>) => {
    setIsFetching(true);
    router.get(
      pathWithYearContext(year_contexts_path),
      {
        filter: {
          sort_key: sortStatus.columnAccessor,
          sort_direction: sortStatus.direction,
        },
      },
      {
        onFinish: () => setIsFetching(false),
      },
    );
  };

  return (
    <ContentShell
      title="Jahrgänge"
      actions={
        <Button leftSection={<IconPlus />} variant="light" onClick={onNew}>
          Neu
        </Button>
      }
    >
      <YearContextsTable
        years={years}
        onDelete={onDelete}
        onEdit={onEdit}
        onSortStatusChange={sortChange}
        sortStatus={sortStatus}
        fetching={isFetching}
      />
    </ContentShell>
  );
}
